import { Link } from "react-router-dom"
import lobbyBackground from "@/images/backgrounds/lobby-background.webp"
import dealMasterLogo from "@/images/backgrounds/logo-without-dice.png"

// index.css is imported after Tailwind's utilities (see App.tsx), so the shared
// `.btn` class wins the cascade against any `bg-*` / `text-*` utility sitting
// beside it — every `.btn` on these screens came out the same red no matter
// what colour it asked for. The auth buttons carry their own colours instead.
export const authButton =
  "flex w-full items-center justify-center gap-3 mobile:gap-5 rounded-full font-third font-semibold " +
  // min-h in px, not rem: index.css scales the root font size down to 50% under
  // 1000px, so a rem-based height would shrink exactly where the tap target
  // needs to stay at the 44px phones expect.
  "py-3 text-xl mobile:py-5 mobile:text-4xl mobile:min-h-[44px] transition-all duration-150 cursor-pointer " +
  "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"

export const authPrimaryButton = `${authButton} bg-red-500 text-white shadow-[0_6px_16px_rgba(225,6,19,0.35)] hover:-translate-y-0.5 hover:brightness-110 mobile:bg-gradient-button mobile:border mobile:border-amber-200/40 mobile:shadow-[0_10px_30px_rgba(225,6,19,0.55),0_0_24px_rgba(251,191,36,0.3)]`

export const authGhostButton = `${authButton} border-2 border-brown-700 bg-transparent text-brown-700 hover:-translate-y-0.5 hover:bg-brown-700 hover:text-primary mobile:border-amber-400/60 mobile:text-amber-200 mobile:shadow-[0_0_22px_rgba(251,191,36,0.18)] mobile:hover:bg-amber-400 mobile:hover:text-black`

// The app store build's login screen is two controls and nothing else, so on a
// landscape phone they can stand taller than the 44px minimum the screens that
// are fighting for height have to settle for.
export const authTallControl =
  "sm:mobile:!min-h-[52px] sm:mobile:!text-[19px]/[1.2]"

// `.input1` brings its own cream-on-brown palette, which is right on the warm
// desktop column and invisible on the dark glass card a phone gets — hence the
// `!` overrides, which are the only thing that outranks it.
export const authInput =
  "input1 mobile:!min-h-[44px] mobile:!border-white/20 mobile:!bg-white/10 mobile:!py-5 mobile:!text-4xl mobile:!text-white mobile:placeholder:!text-white/50 " +
  "mobile:focus:!border-amber-300/70 mobile:focus:!bg-white/20"

export const authMutedText =
  "font-third text-base text-gray-500 mobile:text-3xl mobile:text-primary/80"

export const authInlineLink =
  "font-semibold text-red-500 underline underline-offset-4 transition-colors hover:text-brown-700 mobile:text-amber-300 mobile:hover:text-amber-200"

// The quiet link that sits beside a field rather than under the form.
export const authSubtleLink =
  "font-third text-base text-gray-500 underline-offset-4 transition-colors hover:text-brown-700 hover:underline mobile:text-3xl mobile:text-primary/70 mobile:hover:text-amber-300"

export const AuthEyeIcon = ({ open }: { open: boolean }) => (
  <svg
    className="h-5 w-5 mobile:h-9 mobile:w-9"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {open ? (
      <>
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M10.6 6.2A9.9 9.9 0 0 1 12 6c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2M6.5 7.7A17.5 17.5 0 0 0 2 13s3.6 7 10 7a9.6 9.6 0 0 0 4.3-1" />
        <path d="M3 3l18 18" />
      </>
    )}
  </svg>
)

// The button that reveals a password field. Absolutely positioned, so the input
// it belongs to has to claim the room for it with `!pr-16 mobile:!pr-24` —
// `.input1` bakes in its own `px-6` and outranks a plain Tailwind utility.
export const AuthPasswordToggle: React.FC<{
  open: boolean
  onToggle: () => void
}> = ({ open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={open ? "Hide password" : "Show password"}
    className="absolute right-0 top-0 flex h-full w-14 cursor-pointer items-center justify-center text-gray-500 transition-colors hover:text-brown-700 mobile:w-20 mobile:text-white/60 mobile:hover:text-white"
  >
    <AuthEyeIcon open={open} />
  </button>
)

// The art backdrop a phone gets on every auth screen. The paper texture these
// screens used to sit on read like a web form rather than a game on a phone, so
// here the lobby art carries the screen instead — the same picture the player
// lands on right after signing in.
export const AuthPhoneBackdrop = () => (
  <div className="pointer-events-none absolute inset-0 hidden mobile:block">
    <img
      src={lobbyBackground}
      className="h-full w-full object-cover brightness-[1.9] saturate-[1.25]"
      alt=""
    />
    {/* A warm pool of light where the card sits, a top-to-bottom scrim so light
        text holds up over the bright part of the board, and a vignette so the
        corners stop competing with the card edges. */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(199,99,50,0.4),transparent_68%)]"></div>
    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/60"></div>
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.7)_100%)]"></div>
  </div>
)

// What replaced the paper texture on the desktop column: the same cream the
// palette already calls `primary`, washed lighter at the top so the column has
// some depth rather than reading as one flat rectangle. Hidden on phones, which
// hand the background over to AuthPhoneBackdrop.
export const AuthWarmBackdrop = () => (
  <>
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#fcf5e8] via-primary to-[#e7d2b3] mobile:hidden"></div>
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.75),transparent_65%)] mobile:hidden"></div>
  </>
)

// The card the form sits in: bare on the warm desktop column, a glass panel
// floating on the art at phone widths. `px-*`/`py-*` rather than `p-*` on
// purpose — Tailwind emits the `p` shorthand before `px`/`py`, so a `mobile:p-6`
// would lose to any `px-*` sitting beside it.
export const authCard =
  "relative m-auto w-full mobile:rounded-[28px] mobile:border mobile:border-amber-200/25 mobile:bg-black/60 " +
  // Raw px rather than `px-6`/`py-6`: index.css puts the root font at 8px below
  // 1000px, so every rem-based spacing utility lands at half what it reads as —
  // a `px-6` here was 12px of padding, which is what made the card feel cramped.
  "mobile:px-[18px] mobile:py-[18px] sm:mobile:!px-[22px] sm:mobile:!py-[14px] " +
  // A warm pool of light behind the logo, painted as the card's own background
  // image so it stays under the text without a second element to stack.
  "mobile:bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.15),transparent_65%)] " +
  // Drop shadow, an amber halo that lifts the card off the art, a gold hairline
  // along the top edge and a faint warm wash inside.
  "mobile:shadow-[0_28px_80px_rgba(0,0,0,0.7),0_0_48px_rgba(245,158,11,0.16),inset_0_1px_0_rgba(251,191,36,0.45),inset_0_0_60px_rgba(251,191,36,0.06)] " +
  "mobile:backdrop-blur-xl"

interface AuthLayoutProps {
  /** Desktop-only art for the marketing half. */
  wallpaper: string
  marketingTitle: string
  marketingBody: React.ReactNode
  marketingCta?: { to: string; label: string; testId?: TestId }
  /** Set in the red display face. Hidden on a short phone, where it is the
      first thing that can go to buy the form a line. */
  heading: string
  subheading?: React.ReactNode
  /** For a form long enough that the line of welcome costs it the submit
      button on a landscape phone. The heading already goes at that size; this
      is the next thing that can. */
  hideSubheadingOnShort?: boolean
  /** Login lays its form out in two columns on a short screen and needs the
      extra width; the other screens are a single column. */
  wide?: boolean
  /** The app store build of Login is two ways in and nothing else, which leaves
      a landscape phone with far more width than that stack needs. Set this and
      the brand block stands up as a column of its own beside the form instead
      of a strip above it. */
  brandAside?: boolean
  /** Sits under the form, inside the card. */
  footer?: React.ReactNode
  children: React.ReactNode
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  wallpaper,
  marketingTitle,
  marketingBody,
  marketingCta,
  heading,
  subheading,
  hideSubheadingOnShort,
  wide,
  brandAside,
  footer,
  children,
}) => {
  return (
    <div className="relative grid h-full w-full grid-cols-2 mobile:grid-cols-1">
      <AuthPhoneBackdrop />

      {/* Marketing half. Hidden at phone widths, where the column would be too
          narrow to read and the form is the only thing anyone came for. */}
      <div className="relative flex h-full flex-col justify-between overflow-hidden bg-cover bg-center mobile:hidden">
        <img
          src={wallpaper}
          className="absolute left-0 top-0 -z-20 h-full w-full object-cover contrast-110"
          alt="Deal Master wallpaper"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/40 via-black/25 to-black/70"></div>
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.16),transparent_62%)]"></div>

        <div className="flex w-full justify-center px-8 pt-12 sm:pt-6">
          <img
            src={dealMasterLogo}
            className="w-full max-w-[420px] drop-shadow-[0_10px_30px_rgba(0,0,0,0.9)]"
            alt="Deal Master"
          />
        </div>

        <div className="mx-10 mb-12 rounded-3xl border border-white/20 bg-black/50 px-10 py-10 shadow-[0_16px_42px_rgba(0,0,0,0.45)] backdrop-blur-sm sm:mb-6 sm:py-6">
          <div className="mb-5 text-center font-secondary text-5xl font-bold leading-tight text-white drop-shadow-[0_3px_8px_rgba(0,0,0,0.65)] sm:mb-3">
            {marketingTitle}
          </div>
          <div className="mb-9 flex flex-col items-center gap-2 text-center font-secondary text-xl leading-relaxed text-slate-200 sm:mb-5">
            {marketingBody}
          </div>

          {marketingCta ? (
            <div className="flex justify-center">
              <Link to={marketingCta.to}>
                <button
                  className="cursor-pointer rounded-full border border-amber-200/60 bg-amber-400 px-12 py-4 font-third text-xl font-bold text-slate-950 shadow-[0_8px_18px_rgba(0,0,0,0.45)] transition-all duration-150 hover:-translate-y-0.5 hover:bg-amber-300 sm:py-3"
                  data-testid={marketingCta.testId}
                >
                  {marketingCta.label}
                </button>
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      {/* Form half. Scrolls on its own so a short landscape screen never clips
          the bottom of a long form off the page. */}
      <div className="relative flex h-full w-full flex-col items-center overflow-y-auto bg-primary px-8 py-12 sm:py-6 mobile:z-10 mobile:bg-transparent mobile:pb-[max(16px,var(--safe-bottom))] mobile:pl-[max(20px,var(--safe-left))] mobile:pr-[max(20px,var(--safe-right))] mobile:pt-[max(16px,var(--safe-top))]">
        <AuthWarmBackdrop />

        <div
          // Same 420px column on every screen so the form does not jump width
          // as a player moves between them; only a short screen, where the wide
          // pages go two-up, gets more room.
          className={`${authCard} max-w-[420px] ${
            wide
              ? "sm:max-w-[780px] sm:mobile:!max-w-[700px]"
              : "sm:mobile:!max-w-[560px]"
          } ${
            brandAside
              ? // Two columns of content instead of one tall stack, so the card
                // can finally afford padding on a 390px-tall viewport.
                "sm:mobile:!grid sm:mobile:!grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] sm:mobile:!gap-x-[26px] sm:mobile:!px-[30px] sm:mobile:!py-[24px]"
              : ""
          }`}
        >
          {/* Stacked everywhere except a landscape phone. There the logo and
              the line of welcome sit side by side, which buys back ~26px of
              height on a 390px-tall viewport — or, with brandAside, stand as
              the left column of the card with a gold rule down their edge. */}
          <div
            className={
              brandAside
                ? // A pool of warm light behind the logo so the brand column reads as
                  // the lit half of the card rather than dead space beside
                  // the controls.
                  "sm:mobile:flex sm:mobile:flex-col sm:mobile:items-center sm:mobile:justify-center sm:mobile:border-r sm:mobile:border-amber-200/25 sm:mobile:pr-[26px] sm:mobile:bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.13),transparent_70%)] sm:mobile:!mb-0"
                : "sm:mobile:flex sm:mobile:items-center sm:mobile:justify-center sm:mobile:gap-[18px] sm:mobile:!mb-[12px]"
            }
          >
            <img
              src={dealMasterLogo}
              className={`mx-auto hidden w-full max-w-[250px] drop-shadow-[0_8px_28px_rgba(0,0,0,0.8)] mobile:mb-[14px] mobile:block sm:!max-w-[120px] ${
                brandAside
                  ? // The one piece of art on the screen, so it gets to be the
                    // size the column allows rather than the 104px a strip
                    // above the form could spare.
                    "sm:mobile:!mb-[16px] sm:mobile:!max-w-[205px] sm:mobile:drop-shadow-[0_10px_34px_rgba(0,0,0,0.9)]"
                  : "sm:mobile:!mx-0 sm:mobile:!mb-0 sm:mobile:!max-w-[104px] sm:mobile:shrink-0"
              }`}
              alt="Deal Master"
            />

            {/* Cream rather than the red this heading uses on the warm column:
                `.form-heading` keeps its red offset shadow, which turns into a
                glow behind the light letters and reads far better over the art. */}
            <div className="form-heading mb-2 text-center uppercase sm:mb-1 mobile:mb-[8px] mobile:!text-primary mobile:drop-shadow-[0_4px_18px_rgba(225,6,19,0.55)] sm:mobile:hidden">
              {heading}
            </div>
            {subheading ? (
              <div
                className={`mb-8 text-center sm:mb-4 mobile:mb-[18px] sm:mobile:!mb-0 ${authMutedText} ${
                  brandAside
                    ? "sm:mobile:!text-[13px]/[1.5] sm:mobile:text-amber-100/85 sm:mobile:[text-wrap:balance]"
                    : ""
                } ${hideSubheadingOnShort ? "sm:mobile:hidden" : ""}`}
              >
                {subheading}
              </div>
            ) : null}
          </div>

          {children}

          {footer ? (
            <div className={brandAside ? "sm:mobile:col-span-2" : ""}>
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default AuthLayout
